import { useState } from "react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { X, CheckCircle2 } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useContacts } from "@/hooks/api/useContacts";
import { useCreateContactLog, useContactLogsByContact } from "@/hooks/api/useContactLogs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const formSchema = z.object({
  contact: z.number({
    required_error: "Please select a contact",
  }),
  content: z.string().min(1, "Memory content is required"),
  location: z.string().optional(),
  mood: z.string().optional(),
});

interface AddMemorySectionProps {
  onClose: () => void;
  onSuccess?: () => void;
  onFormStateChange?: (state: {
    contactId: number | undefined;
    contactName: string;
    contactAvatar: string;
    memories: any[];
  }) => void;
}

export const AddMemorySection = ({ onClose, onSuccess, onFormStateChange }: AddMemorySectionProps) => {
  const { toast } = useToast();
  const { data: contactsData } = useContacts();
  const createContactLog = useCreateContactLog();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      content: "",
      location: "",
      mood: "",
    },
  });

  const contacts = contactsData?.results || [];

  // Watch selected contact
  const selectedContactId = form.watch('contact');

  // Fetch contact logs for selected contact
  const { data: contactLogsData } = useContactLogsByContact(
    selectedContactId,
    !!selectedContactId
  );

  // Watch form values and notify parent of selected contact
  const watchedValues = form.watch();
  React.useEffect(() => {
    const selectedContact = contacts.find(c => c.id === watchedValues.contact);

    if (selectedContact) {
      onFormStateChange?.({
        contactId: watchedValues.contact,
        contactName: selectedContact?.name || '',
        contactAvatar: selectedContact?.avatar || '',
        memories: contactLogsData || [],
      });
    } else {
      onFormStateChange?.({
        contactId: undefined,
        contactName: '',
        contactAvatar: '',
        memories: [],
      });
    }
  }, [watchedValues.contact, contacts, contactLogsData, onFormStateChange]);

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsSubmitting(true);

      await createContactLog.mutateAsync({
        contact: values.contact,
        content: values.content,
        location: values.location || undefined,
        mood: values.mood || undefined,
      });

      setShowSuccessModal(true);
      form.reset();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error?.message || "Failed to add memory. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    onSuccess?.();
    handleClose();
  };

  return (
    <div className="bg-white rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-[20px] font-bold font-euclid text-wabisabi-plum">Add Memory</h3>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className="h-8 w-8"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {/* Contact Selection */}
          <FormField
            control={form.control}
            name="contact"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#2C2C2C] text-[16px] font-normal">Who is this memory about?</FormLabel>
                <Select
                  onValueChange={(value) => field.onChange(Number(value))}
                  value={field.value?.toString()}
                >
                  <FormControl>
                    <SelectTrigger className="h-auto px-4 py-4 rounded-[10px] border-[#DDDDDD] focus:border-[#6C4F77] focus:ring-0">
                      <SelectValue placeholder="Select a contact" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {contacts.map((contact) => (
                      <SelectItem key={contact.id} value={contact.id.toString()}>
                        {contact.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Memory Content */}
          <FormField
            control={form.control}
            name="content"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#2C2C2C] text-[16px] font-normal">What do you want to remember?</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Describe this memory..."
                    className="resize-none"
                    rows={4}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Mood (Optional) */}
          <FormField
            control={form.control}
            name="mood"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#2C2C2C] text-[16px] font-normal">How did it make you feel?</FormLabel>
                <FormControl>
                  <Input placeholder="How did you feel?" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Location (Optional) */}
          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-[#2C2C2C] text-[16px] font-normal">Where did this happen?</FormLabel>
                <FormControl>
                  <Input placeholder="Where did this happen?" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Actions */}
          <div className="flex justify-start gap-3 pt-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="bg-wabisabi-plum hover:bg-wabisabi-plum/90 text-white"
            >
              {isSubmitting ? "Saving..." : "Save memory"}
            </Button>
          </div>
        </form>
      </Form>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex flex-col items-center justify-center gap-4 py-6">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
              </div>
              <DialogTitle className="text-2xl font-bold text-center">
                Thank you for sharing this memory 💛
              </DialogTitle>
              <DialogDescription className="text-center text-gray-600">
                We are working our magic in the background to create personalized suggestions, recommendations, reminders and a lot more.
                <br /><br />
                Sit back and relax - we'll notify you via email when they're ready.
              </DialogDescription>
            </div>
          </DialogHeader>
          <div className="flex justify-center">
            <Button
              onClick={handleSuccessModalClose}
              className="bg-wabisabi-plum hover:bg-wabisabi-plum/90 text-white px-8"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
